import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { UserPlus, UserCheck, Clock, Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
import { localizeMessage } from '../utils/toast.js';
import './FollowButton.css';

const API_BASE_URL = `${GREENWICH_BACKEND_URL}/api`;

const FollowButton = ({ userId, username, profileImage, onFollowChange }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState({
    isFollowing: false,
    isFollower: false,
    isMutual: false,
    hasPendingRequest: false,
    followRequestsEnabled: true,
    requestId: null,
    outgoingRequestId: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    console.log("FollowButton received userId:", userId); // Debugging
    if (userId && userId !== "undefined") {
      checkFollowStatus();
    } else {
      console.warn("User ID is undefined or invalid, skipping follow status check");
    }
  }, [userId]);

  const getToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn("No authentication token found");
    }
    return token;
  };

  const checkFollowStatus = async () => {
    try {
      const token = getToken();
      if (!token) return;

      // Validate userId before making the request
      if (!userId || userId === "undefined" || userId === undefined) {
        console.error("Invalid user ID for follow status check:", userId);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/follow/status/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStatus(data.status);
        }
      } else {
        console.error('Failed to check follow status:', response.status);
      }
    } catch (error) {
      console.error('Error checking follow status:', error);
    }
  };

  const sendFollowRequest = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = getToken();
      if (!token) {
        setError(t('followButton.pleaseLogin'));
        setLoading(false);
        return;
      }

      // ✅ Ավելացրեք ստուգում userId-ի համար
      if (!userId || userId === "undefined" || userId === undefined) {
        setError(t('followButton.userIdMissing'));
        setLoading(false);
        return;
      }

      console.log("Sending follow request to user ID:", userId); // Debugging

      const response = await fetch(`${API_BASE_URL}/follow/request/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage(data.message || t('followButton.requestSent'));
        await checkFollowStatus();
        if (onFollowChange) onFollowChange();

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(data.error || t('followButton.failedSendRequest'));
      }
    } catch (error) {
      console.error('Error sending follow request:', error);
      setError(t('followButton.failedSendRequest'));
    } finally {
      setLoading(false);
    }
  };

  const acceptFollowRequest = async () => {
    try {
      setLoading(true);
      const token = getToken();

      if (!status.requestId) {
        setError(t('followButton.noRequestId'));
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/follow/accept/${status.requestId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSuccessMessage(t('followButton.requestAccepted'));
        await checkFollowStatus();
        if (onFollowChange) onFollowChange();

        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        const data = await response.json();
        setError(data.error || t('followButton.failedAccept'));
      }
    } catch (error) {
      console.error('Error accepting follow request:', error);
      setError(t('followButton.failedAccept'));
    } finally {
      setLoading(false);
    }
  };

  const rejectFollowRequest = async () => {
    try {
      setLoading(true);
      const token = getToken();

      if (!status.requestId) {
        setError(t('followButton.noRequestId'));
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/follow/reject/${status.requestId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSuccessMessage(t('followButton.requestRejected'));
        await checkFollowStatus();
        if (onFollowChange) onFollowChange();

        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        const data = await response.json();
        setError(data.error || t('followButton.failedReject'));
      }
    } catch (error) {
      console.error('Error rejecting follow request:', error);
      setError(t('followButton.failedReject'));
    } finally {
      setLoading(false);
    }
  };

  const unfollowUser = async () => {
    try {
      setLoading(true);
      const token = getToken();

      if (!userId || userId === "undefined") {
        setError(t('followButton.unfollowIdMissing'));
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/unfollow/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSuccessMessage(t('followButton.unfollowedSuccess'));
        await checkFollowStatus();
        if (onFollowChange) onFollowChange();

        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        const data = await response.json();
        setError(data.error || t('followButton.failedUnfollow'));
      }
    } catch (error) {
      console.error('Error unfollowing user:', error);
      setError(t('followButton.failedUnfollow'));
    } finally {
      setLoading(false);
    }
  };

  const cancelFollowRequest = async () => {
    try {
      setLoading(true);
      const token = getToken();

      if (!status.outgoingRequestId) {
        setError(t('followButton.noRequestIdCancel'));
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/follow/request/${status.outgoingRequestId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSuccessMessage(t('followButton.requestCancelled'));
        await checkFollowStatus();
        if (onFollowChange) onFollowChange();

        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        const data = await response.json();
        setError(data.error || t('followButton.failedCancel'));
      }
    } catch (error) {
      console.error('Error cancelling follow request:', error);
      setError(t('followButton.failedCancel'));
    } finally {
      setLoading(false);
    }
  };

  const renderButton = () => {
    if (!userId || userId === "undefined") {
      return (
        <button className="follow-btn disabled-btn" disabled>
          <UserPlus size={16} />
          {t('followButton.userUnavailable')}
        </button>
      );
    }

    if (loading) {
      return (
        <button className="follow-btn loading" disabled>
          <div className="spinner"></div>
          {t('followButton.loading')}
        </button>
      );
    }

    // Եթե օգտատերը մեզ ուղարկել է follow request (մենք պետք է հաստատենք/մերժենք)
    if (status.isFollower && !status.isFollowing && status.hasPendingRequest) {
      return (
        <div className="request-actions">
          <button className="follow-btn accept-btn" onClick={acceptFollowRequest}>
            <Check size={16} />
            {t('followButton.acceptRequest')}
          </button>
          <button className="follow-btn reject-btn" onClick={rejectFollowRequest}>
            <X size={16} />
            {t('followButton.reject')}
          </button>
        </div>
      );
    }

    // Եթե մենք հետևում ենք օգտատիրոջը
    if (status.isFollowing) {
      return (
        <div className="following-actions">
          {status.isMutual ? (
            <span className="mutual-badge">
              <UserCheck size={16} />
              {t('followButton.mutualFollow')}
            </span>
          ) : (
            <span className="following-badge">
              <UserCheck size={16} />
              {t('followButton.following')}
            </span>
          )}
          <button className="follow-btn unfollow-btn" onClick={unfollowUser}>
            {t('followButton.unfollow')}
          </button>
        </div>
      );
    }

    // Եթե մենք ուղարկել ենք follow request և սպասում ենք
    if (status.hasPendingRequest) {
      return (
        <button className="follow-btn pending-btn" onClick={cancelFollowRequest}>
          <Clock size={16} />
          {t('followButton.requestSentBtn')}
        </button>
      );
    }

    // Default - հետևելու կոճակ
    return (
      <button className="follow-btn follow-btn-primary" onClick={sendFollowRequest}>
        <UserPlus size={16} />
        {t('followButton.follow')}
      </button>
    );
  };

  return (
    <div className="follow-button-container">
      {error && (
        <div className="error-message" onClick={() => setError(null)}>
          {localizeMessage(error)}
        </div>
      )}
      {successMessage && (
        <div className="success-message" onClick={() => setSuccessMessage(null)}>
          {successMessage}
        </div>
      )}

      <div className="follow-button-wrapper">
        {renderButton()}
      </div>
    </div>
  );
};

FollowButton.propTypes = {
  userId: PropTypes.string.isRequired,
  username: PropTypes.string,
  profileImage: PropTypes.string,
  onFollowChange: PropTypes.func
};

FollowButton.defaultProps = {
  username: '',
  profileImage: '',
  onFollowChange: () => {}
};

export default FollowButton;
